<script setup lang="ts">
import { Mask } from 'maska';

type Props = {
  getValuesFromUser?: boolean;
};

defineProps<Props>();

const { t } = useTexts();
const { registerData, validationData } = storeToRefs(useRegisterStore());
const { $reset: $resetRegisterStore } = useRegisterStore();
const {
  userId,
  userAddressZipcode,
  userCountry,
  userAddressFullAddress,
  userAddressCity,
  userAddressState,
} = useUserData();

const userCanChangeCountry = ref(false);
const zipCodeField = ref(registerData.value.zipcode);
const zipCityFoundByApi = ref(false);
const zipStateFoundByApi = ref(false);
const isZipLoading = ref(false);
const addressLabelRef = ref<HTMLLabelElement | null>(null);
const isMounted = ref(false);

const maskByCountryZipCode = computed<string[] | string>(() => {
  return userCountry.value === 'BRA' ? '#####-###' : '';
});

const unmaskedZipCode = computed<string>(() => {
  const mask = new Mask({ mask: maskByCountryZipCode.value });

  return mask.unmasked(zipCodeField.value);
});

function checkZipCodeField() {
  if (userCountry.value !== 'BRA') {
    return;
  }

  if (unmaskedZipCode.value.length === 8) {
    registerData.value.zipcode = unmaskedZipCode.value;
    validationData.value.fields.zipcode = true;
    getZipInfos();
  } else {
    validationData.value.fields.zipcode = false;
  }
}

async function getZipInfos() {
  if (!isMounted.value) return;

  isZipLoading.value = true;

  try {
    const { ApiService } = useApiService();

    const { status, data } = await ApiService.post('apicep', {
      cep: unmaskedZipCode.value,
      user_id: userId.value || '',
    });

    if (status === 200) {
      registerData.value.address = data.street
        ? data.street
        : data.logradouro
          ? data.logradouro
          : '';
      if (data.city || data.cidade) {
        registerData.value.city = data.city
          ? data.city
          : data.cidade
            ? data.cidade
            : '';
        zipCityFoundByApi.value = true;
      }
      if (data.uf) {
        registerData.value.state = data.uf;
        zipStateFoundByApi.value = true;
      }
    } else {
      registerData.value.address = '';
      registerData.value.city = '';
      registerData.value.state = '0';
      zipCityFoundByApi.value = false;
      zipStateFoundByApi.value = false;
    }
  } catch (error) {
    zipCityFoundByApi.value = false;
    zipStateFoundByApi.value = false;
  } finally {
    checkAddressField();
    checkCityField();
    checkStateField();
    addressLabelRef.value?.click();
    isZipLoading.value = false;
  }
}

function checkAddressField() {
  validationData.value.fields.address = registerData.value.address.length >= 3;
}

function checkCityField() {
  validationData.value.fields.city = registerData.value.city.length >= 2;
}

function checkStateField() {
  validationData.value.fields.state = registerData.value.state.length === 2;
}

const allBrazilianStates = [
  { value: '0', label: t('general.select') },
  { value: 'AC', label: 'Acre (AC)' },
  { value: 'AL', label: 'Alagoas (AL)' },
  { value: 'AP', label: 'Amapá (AP)' },
  { value: 'AM', label: 'Amazonas (AM)' },
  { value: 'BA', label: 'Bahia (BA)' },
  { value: 'CE', label: 'Ceará (CE)' },
  { value: 'DF', label: 'Distrito Federal (DF)' },
  { value: 'ES', label: 'Espírito Santo (ES)' },
  { value: 'GO', label: 'Goiás (GO)' },
  { value: 'MA', label: 'Maranhão (MA)' },
  { value: 'MT', label: 'Mato Grosso (MT)' },
  { value: 'MS', label: 'Mato Grosso do Sul (MS)' },
  { value: 'MG', label: 'Minas Gerais (MG)' },
  { value: 'PA', label: 'Pará (PA)' },
  { value: 'PB', label: 'Paraíba (PB)' },
  { value: 'PR', label: 'Paraná (PR)' },
  { value: 'PE', label: 'Pernambuco (PE)' },
  { value: 'PI', label: 'Piauí (PI)' },
  { value: 'RJ', label: 'Rio de Janeiro (RJ)' },
  { value: 'RN', label: 'Rio Grande do Norte (RN)' },
  { value: 'RS', label: 'Rio Grande do Sul (RS)' },
  { value: 'RO', label: 'Rondônia (RO)' },
  { value: 'RR', label: 'Roraima (RR)' },
  { value: 'SC', label: 'Santa Catarina (SC)' },
  { value: 'SP', label: 'São Paulo (SP)' },
  { value: 'SE', label: 'Sergipe (SE)' },
  { value: 'TO', label: 'Tocantins (TO)' },
];

watch(
  () => [registerData.value.state],
  () => {
    checkStateField();
  },
);
watch(() => unmaskedZipCode.value, checkZipCodeField);

const hasError = computed(() => {
  const validationFields = validationData.value.fields;
  const validationMap = ['zipcode', 'address', 'city', 'state', 'country_data'];

  return (
    validationMap.some(
      (field: string) =>
        !validationFields[field as keyof typeof validationFields],
    ) ?? false
  );
});

const anyChanges = computed(() => {
  const registerFields = registerData.value;

  return (
    registerFields.zipcode !== userAddressZipcode.value ||
    registerFields.address !== userAddressFullAddress.value ||
    registerFields.state !== userAddressState.value ||
    registerFields.city !== userAddressCity.value ||
    registerFields.country_data !== userCountry.value
  );
});

defineExpose({
  hasError,
  anyChanges,
});

onMounted(() => {
  nextTick(() => {
    isMounted.value = true;
  });
});

onUnmounted(() => {
  $resetRegisterStore();
});
</script>

<template>
  <section class="flex gap-5">
    <div class="flex-grow relative">
      <label
        class="registerLabel"
        for="zipcode"
      >
        {{ $t('account.user_data.zipcode') }} <span>*</span>
      </label>
      <BaseFormInput
        id="zipcode"
        v-model="zipCodeField"
        :data-maska="maskByCountryZipCode"
        :disabled="isZipLoading"
        :initial-value="
          getValuesFromUser ? userAddressZipcode : registerData.zipcode
        "
        name="zipcode"
        placeholder="ex: 98765-123"
        type="tel"
      >
      </BaseFormInput>
      <span
        v-if="isZipLoading"
        class="absolute bottom-0 right-0 mb-4 mr-6"
      >
        <NuxtIcon
          class="animate-spin w-4 h-4 mx-auto text-texts/40"
          name="loading"
        />
      </span>
    </div>

    <div class="flex-grow">
      <label
        class="registerLabel"
        for="userCountry"
      >
        {{ $t('account.user_data.country') }} <span>*</span>
      </label>
      <div
        :class="[
          $style.editableField,
          { [$style.isDisabled]: !userCanChangeCountry },
        ]"
      >
        <BaseFormSelectCountry
          ref="selectCountryRef"
          :show-label="false"
          :user-can-change-country="userCanChangeCountry"
          :with-check="true"
        />
      </div>
    </div>
  </section>

  <label
    ref="addressLabelRef"
    class="registerLabel"
    for="address"
  >
    {{ $t('account.user_data.full_address') }} <span>*</span>
  </label>
  <BaseFormInput
    id="address"
    v-model="registerData.address"
    :initial-value="
      getValuesFromUser ? userAddressFullAddress : registerData.address
    "
    name="address"
    placeholder="ex: Rua das Flores, 567, Ap 1202"
    type="text"
    @input="checkAddressField"
  />

  <section class="flex gap-5">
    <div class="flex-grow">
      <label
        class="registerLabel"
        for="city"
      >
        {{ $t('account.user_data.city') }} <span>*</span>
      </label>

      <BaseFormInput
        id="city"
        v-model="registerData.city"
        :disabled="zipCityFoundByApi"
        :initial-value="getValuesFromUser ? userAddressCity : registerData.city"
        name="city"
        placeholder="ex: Belo Horizonte"
        type="text"
        @input="checkCityField"
      />
    </div>

    <div class="flex-grow">
      <label
        class="registerLabel"
        for="state"
      >
        {{ $t('account.user_data.state') }} <span>*</span>
      </label>

      <BaseFormSelectField
        v-if="userCountry === 'BRA'"
        id="state"
        v-model="registerData.state"
        :disabled="zipStateFoundByApi"
        :initial-value="getValuesFromUser ? userAddressState : '0'"
        name="state"
        :options="allBrazilianStates"
        placeholder="ex: MG"
      />
      <BaseFormInput
        v-else
        id="state"
        v-model="registerData.state"
        name="state"
        placeholder="ex: MG"
        type="text"
      />
    </div>
  </section>
</template>

<style module lang="scss">
.editableField {
  @apply bg-auth-bg-inputs h-[46px] sm:h-[50px] rounded-md flex items-center relative;

  &.isDisabled {
    @apply cursor-not-allowed;
  }
}
</style>
